import { Box, Button, IconButton, Tooltip } from '@mui/material'
import { type RowSelectionState } from '@tanstack/react-table'
import {
  CloudDownload as CloudDownloadIcon,
  CopyAll as CopyAllIcon,
  FolderZip as FolderZipIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import {
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  useCallback,
  useRef,
} from 'react'
import {
  fetchTrxStreamsPaginated,
  downloadTrxStream,
  downloadTrxStreamsZip,
  MAX_ZIP_DOWNLOAD_FILES,
} from 'api/trx'

import { TEST_IDS } from 'test-ids'
import colors from 'common/colors'

import TrxStreamFilterBar from './filters'
import { runWithConcurrencyLimit, saveBlob } from './download-utils'
import LogViewerComponent from 'components/farm-settings/log-viewer'
import type { LogLink } from 'components/farm-settings/log-viewer/log-link'
import HarvestPicker from './harvest-picker'
import { getSelectedRows } from './table-utils'
import TrxStreamTable, {
  trxStreamColumnHelper,
  type TrxStreamColumn,
  type TrxStreamPagination,
} from './trx-stream-table'
import {
  type TrxStreamData,
  type TrxStreamFilters,
  DEFAULT_TRX_STREAM_FILTERS,
} from './types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500, 1000]
const GROW_COLUMN_ID = 's3Key'
// Keeps the per-row copy button within the table's compact row height.
const ROW_ICON_BUTTON_PADDING = '2px'
// Each selected file is a separate API/S3 request and browser download; a
// full 1000-row selection must not fire them all at once. Stream files are
// ~4 kB, so a wide batch keeps large selections quick.
const INDIVIDUAL_DOWNLOAD_CONCURRENCY = 100

function formatRelativeDate(value: string, locale: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000)
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 60 * 60 * 24 * 365],
    ['month', 60 * 60 * 24 * 30],
    ['week', 60 * 60 * 24 * 7],
    ['day', 60 * 60 * 24],
    ['hour', 60 * 60],
    ['minute', 60],
    ['second', 1],
  ]
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  for (const [unit, unitSeconds] of units) {
    if (Math.abs(diffSeconds) >= unitSeconds || unit === 'second') {
      return rtf.format(Math.round(diffSeconds / unitSeconds), unit)
    }
  }
  return value
}

export default function TrxStreamComponent({
  linkRequest,
  linkTenantId,
  clearLink,
}: {
  linkRequest: LogLink | null
  linkTenantId?: string
  clearLink: () => void
}): ReactElement<any, any> {
  const { t, i18n } = useTranslation()

  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(
    null,
  )
  const [isDownloading, setDownloading] = useState(false)

  const [rows, setRows] = useState<TrxStreamData[]>([])
  const [paginationModel, setPaginationModel] = useState<TrxStreamPagination>({
    page: 0,
    pageSize: 10,
  })
  const [filters, setFilters] = useState<TrxStreamFilters>(
    DEFAULT_TRX_STREAM_FILTERS,
  )
  const requestKey = JSON.stringify([
    (paginationModel.page || 0) + 1,
    paginationModel.pageSize,
    filters,
  ])
  const isLoading = completedRequestKey !== requestKey
  // Remounts TrxStreamFilterBar and HarvestPicker on Clear Filters. A
  // remount (rather than just resetting the `filters` prop) is required for
  // the filter bar specifically: if the user clears their typed
  // serialNumber input before its 400ms debounce elapses,
  // filters.serialNumber is already '' (the default) by the time Clear
  // Filters runs, so that prop wouldn't change and the filter bar's
  // debounce-cancellation effect (keyed off it) would never fire, letting
  // the pending callback reapply the stale value afterward.
  const [filtersResetToken, setFiltersResetToken] = useState(0)
  const [rowCount, setRowCount] = useState(0)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const selectedRows = useMemo(
    () => getSelectedRows(rows, rowSelection),
    [rows, rowSelection],
  )
  const [logViewerData, setLogViewerData] = useState<{
    serialNumber: string
    instanceId: number
    recordNumber: number
  } | null>(null)
  const [isLogViewerOpen, setIsLogViewerOpen] = useState(false)
  const handleViewLogs = useCallback((data: any) => {
    setLogViewerData({
      serialNumber: data.serialNumber,
      instanceId: data.instanceId,
      recordNumber: data.recordNumber,
    })
    setIsLogViewerOpen(true)
  }, [])

  const updateFilters = useCallback((patch: Partial<TrxStreamFilters>) => {
    setFilters((f) => ({ ...f, ...patch }))
    setPaginationModel((p) => ({ ...p, page: 0 }))
  }, [])

  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_TRX_STREAM_FILTERS)
    setFiltersResetToken((n) => n + 1)
    setPaginationModel((p) => ({ ...p, page: 0 }))
  }, [])

  const copyS3Key = useCallback(async (s3Key: string) => {
    try {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          'text/plain': new Blob([s3Key], { type: 'text/plain' }),
        }),
      ])
    } catch (e) {
      console.log(e)
    }
  }, [])

  const renderRelativeDateCell = useCallback(
    (value: string | null) => {
      if (!value) return '-'
      return (
        <Tooltip title={value}>
          <span>{formatRelativeDate(value, i18n.language || 'en')}</span>
        </Tooltip>
      )
    },
    [i18n.language],
  )

  const columns = useMemo<TrxStreamColumn[]>(
    () => [
      trxStreamColumnHelper.accessor('serialNumber', {
        header: t('serial_number'),
        size: 220,
      }),
      trxStreamColumnHelper.accessor('instanceId', {
        header: t('instance_id'),
        size: 100,
      }),
      trxStreamColumnHelper.accessor('recordNumber', {
        header: t('record_number'),
        size: 200,
      }),
      trxStreamColumnHelper.accessor('fileType', {
        header: t('type'),
        size: 90,
        cell: (info) => {
          const value = info.getValue()
          if (value === 'log') {
            return (
              <Button
                variant="outlined"
                size="small"
                data-testid={TEST_IDS.trxStreamViewLogsButton}
                key={`log-${value}`}
                onClick={() => handleViewLogs(info.row.original)}
              >
                {t('log')}
              </Button>
            )
          }
          return value
        },
      }),
      trxStreamColumnHelper.accessor('uploadDate', {
        header: t('upload_date'),
        size: 170,
        cell: (info) => renderRelativeDateCell(info.getValue()),
      }),
      trxStreamColumnHelper.accessor('startTime', {
        header: t('start_time'),
        size: 170,
        cell: (info) => renderRelativeDateCell(info.getValue()),
      }),
      trxStreamColumnHelper.accessor('validityStatus', {
        header: t('status'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (info.row.original.fileType !== 'trx' || !value) return '-'
          return t(value)
        },
      }),
      trxStreamColumnHelper.accessor(GROW_COLUMN_ID, {
        header: t('s3_key'),
        size: 280,
        cell: (info) => (
          <Box
            sx={{
              width: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Tooltip title={info.getValue()}>
              <span
                style={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                }}
              >
                {info.getValue()}
              </span>
            </Tooltip>
            <IconButton
              size="small"
              sx={{ p: ROW_ICON_BUTTON_PADDING }}
              title={t('copy')}
              aria-label={t('copy')}
              data-testid={TEST_IDS.trxStreamCopyRowS3KeyButton}
              onClick={() => void copyS3Key(info.getValue() || '')}
            >
              <CopyAllIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      }),
    ],
    [copyS3Key, handleViewLogs, renderRelativeDateCell, t],
  )

  // Passing a boolean `ignore` flag by value doesn't work here: it's
  // captured once when fetchData is called, so mutating the effect's local
  // variable in its cleanup never reaches the already-scheduled .then()
  // callback. A ref-based request generation counter is checked at
  // resolution time instead, so only the most recent request's response
  // (and its loading-state update) is ever applied.
  const latestRequestIdRef = useRef(0)

  const handleCloseLogs = () => {
    setIsLogViewerOpen(false)
    setLogViewerData(null)
  }

  const fetchData = useCallback(
    (page: number, pageSize: number, currentFilters: TrxStreamFilters) => {
      const requestId = ++latestRequestIdRef.current
      const currentRequestKey = JSON.stringify([page, pageSize, currentFilters])
      Promise.resolve().then(() => {
        if (latestRequestIdRef.current === requestId) {
          setCompletedRequestKey(null)
        }
      })
      fetchTrxStreamsPaginated(page, pageSize, currentFilters)
        .then((res) => {
          if (latestRequestIdRef.current !== requestId) return
          const { results, pagination } = res.data
          const _rows: TrxStreamData[] = (results || []).map((row: any) => ({
            id: `${row.fileType}-${row.serialNumber}-${row.instanceId}-${row.recordNumber}`,
            fileType: row.fileType,
            serialNumber: row.serialNumber,
            instanceId: row.instanceId,
            recordNumber: row.recordNumber,
            s3Key: row.s3Key,
            uploadDate: row.uploadDate,
            processedAt: row.processedAt,
            startTime: row.startTime,
            extent: row.extent,
            validityStatus: row.validityStatus,
          }))
          setRows(_rows)
          setRowSelection({})
          setRowCount(pagination?.total || 0)
          setCompletedRequestKey(currentRequestKey)
        })
        .catch(() => {
          if (latestRequestIdRef.current === requestId) {
            setCompletedRequestKey(currentRequestKey)
          }
        })
    },
    [],
  )

  useEffect(() => {
    fetchData(
      (paginationModel.page || 0) + 1,
      paginationModel.pageSize,
      filters,
    )
  }, [paginationModel, filters, fetchData])

  const copyS3Keys = useCallback(async () => {
    if (selectedRows.length > 0) {
      const s3Keys = selectedRows.map((row) => row.s3Key)

      try {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/plain': new Blob([s3Keys.join('\n')], {
              type: 'text/plain',
            }),
          }),
        ])
      } catch (e) {
        console.log(e)
      }
    }
  }, [selectedRows])

  const downloadSelectedStreams = useCallback(async () => {
    if (selectedRows.length === 0) return
    setDownloading(true)
    try {
      await runWithConcurrencyLimit(
        selectedRows,
        INDIVIDUAL_DOWNLOAD_CONCURRENCY,
        async ({ serialNumber, instanceId, recordNumber, fileType }) => {
          const res = await downloadTrxStream(
            instanceId,
            serialNumber.trim(),
            recordNumber,
            fileType,
          )
          saveBlob(
            new Blob([res.data], { type: 'application/octet-stream' }),
            `${serialNumber.trim()}-${instanceId}-${recordNumber}.${fileType}`,
          )
        },
      )
    } finally {
      setDownloading(false)
    }
  }, [selectedRows])

  const downloadSelectedStreamsAsZip = useCallback(async () => {
    if (
      selectedRows.length === 0 ||
      selectedRows.length > MAX_ZIP_DOWNLOAD_FILES
    ) {
      return
    }
    setDownloading(true)
    const files = selectedRows.map((row) => ({
      instanceId: row.instanceId,
      serialNumber: row.serialNumber.trim(),
      recordNumber: row.recordNumber,
      fileType: row.fileType,
    }))

    try {
      const res = await downloadTrxStreamsZip(files)
      saveBlob(
        new Blob([res.data], { type: 'application/zip' }),
        `trx-files-${Date.now()}.zip`,
      )
    } catch (e) {
      console.log(e)
    } finally {
      setDownloading(false)
    }
  }, [selectedRows])

  const downloadLabel = t('download_streams', {
    item: selectedRows.length > 1 ? t('files') : t('file'),
  })
  const canDownloadIndividually = !isDownloading && selectedRows.length > 0
  const canDownloadZip =
    !isDownloading &&
    selectedRows.length > 0 &&
    selectedRows.length <= MAX_ZIP_DOWNLOAD_FILES

  const downloadZipLabel =
    selectedRows.length > MAX_ZIP_DOWNLOAD_FILES
      ? t('download_streams_zip_limit_exceeded', {
          limit: MAX_ZIP_DOWNLOAD_FILES,
        })
      : t('download_streams_zip', {
          item: selectedRows.length > 1 ? t('files') : t('file'),
        })

  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 4,
          width: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h1>{t('trx_streams')} </h1>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Tooltip title={downloadLabel}>
              <span>
                <IconButton
                  disabled={!canDownloadIndividually}
                  onClick={downloadSelectedStreams}
                  aria-label={downloadLabel}
                  data-testid={TEST_IDS.downloadTrxStreamButton}
                  size="large"
                >
                  <CloudDownloadIcon
                    style={{
                      color: canDownloadIndividually ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={downloadZipLabel}>
              <span>
                <IconButton
                  disabled={!canDownloadZip}
                  onClick={downloadSelectedStreamsAsZip}
                  aria-label={downloadZipLabel}
                  data-testid={TEST_IDS.downloadTrxStreamZipButton}
                  size="large"
                >
                  <FolderZipIcon
                    style={{
                      color: canDownloadZip ? colors.brand : 'gray',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              onClick={copyS3Keys}
              title={t('copy_s3_keys')}
              size="large"
              data-testid={TEST_IDS.copyTrxStreamButton}
              disabled={selectedRows.length === 0}
            >
              <CopyAllIcon
                style={{
                  color: selectedRows.length > 0 ? colors.brand : 'gray',
                }}
              />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TrxStreamFilterBar
              key={`filter-bar-${filtersResetToken}`}
              filters={filters}
              onChange={updateFilters}
            />
            <HarvestPicker
              key={`harvest-picker-${filtersResetToken}`}
              onChange={(harvestId) => updateFilters({ harvestId })}
            />
          </Box>
          <Button
            onClick={clearAllFilters}
            data-testid={TEST_IDS.trxStreamClearFiltersButton}
          >
            {t('clear_filters')}
          </Button>
        </Box>

        <Box
          sx={{
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            height: '100%',
          }}
        >
          <div
            style={{
              flexGrow: 1,
              height: '100%',
              width: '100%',
              marginTop: '2rem',
            }}
          >
            <TrxStreamTable
              rows={rows}
              columns={columns}
              growColumnId={GROW_COLUMN_ID}
              rowCount={rowCount}
              isLoading={isLoading}
              pagination={paginationModel}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPaginationChange={setPaginationModel}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
            />
          </div>
          {isLogViewerOpen && logViewerData ? (
            <LogViewerComponent
              onClose={handleCloseLogs}
              hardwareId={logViewerData?.serialNumber}
              instanceId={logViewerData?.instanceId}
              recordNumber={logViewerData?.recordNumber}
              tenantId={linkTenantId}
            />
          ) : linkRequest?.source.type === 'trx' ? (
            <LogViewerComponent
              onClose={clearLink}
              deferPopup
              hardwareId={linkRequest.source.hardwareId}
              instanceId={linkRequest.source.instanceId}
              recordNumbers={linkRequest.source.recordNumbers}
              startLine={linkRequest.startLine}
              endLine={linkRequest.endLine}
              tenantId={linkTenantId}
            />
          ) : null}
        </Box>
      </Box>
    </>
  )
}
